import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import {
  CandlestickChart,
  ChevronDown,
  Coins,
  LayoutDashboard,
  LogOut,
  PanelLeftDashed,
  PiggyBank,
  PieChart,
  ScanSearch,
  Settings,
  Target,
  TrendingUp,
  User,
  type LucideIcon,
} from 'lucide-react';
import { authService, userService, type CurrentUserInfo } from '@/services';
import { useI18n } from '@/i18n/use-i18n';
import { useSidebarStore } from '@/stores/sidebar-store';
import { cn } from '@/utils/cn';
import './sidebar.css';

type NavigationItem = {
  path: string;
  label: string;
  icon: LucideIcon;
};

const getStringValue = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const getEmailPrefix = (email: string): string => {
  const atIndex = email.indexOf('@');
  return atIndex > 0 ? email.slice(0, atIndex) : email;
};

export function Sidebar() {
  const { strings } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { isCollapsed, setCollapsed } = useSidebarStore();
  const navigationItems: NavigationItem[] = [
    { path: '/dashboard', label: strings.sidebar.dashboard, icon: LayoutDashboard },
    { path: '/contributions', label: strings.sidebar.contributions, icon: PiggyBank },
    { path: '/asset-classes', label: strings.sidebar.assetClasses, icon: PieChart },
    { path: '/target-portfolio', label: strings.sidebar.targetPortfolio, icon: Target },
    { path: '/market', label: strings.sidebar.market, icon: CandlestickChart },
    { path: '/proventos', label: strings.sidebar.proventos, icon: Coins },
    { path: '/fii-radar', label: 'FII Radar', icon: ScanSearch },
    { path: '/stock-radar', label: 'Ações Radar', icon: TrendingUp },
  ];
  const { data: currentUser } = useQuery<CurrentUserInfo>({
    queryKey: ['current-user'],
    queryFn: userService.getCurrentUser,
    staleTime: 1000 * 60 * 5,
  });

  const name = getStringValue(currentUser?.name);
  const email = getStringValue(currentUser?.email);
  const displayName = name || (email ? getEmailPrefix(email) : '');
  const primaryLabel = displayName || strings.header.accountFallback;
  //const secondaryLabel = email || strings.header.emailFallback;

  const handleOpenSidebar = () => {
    if (isCollapsed) {
      setCollapsed(false);
    }
  };

  const handleCloseSidebar = () => {
    if (!isCollapsed) {
      setCollapsed(true);
    }
  };

  const handleSignOut = async () => {
    await authService.signOut();
    navigate('/login', { replace: true });
  };

  return (
    <aside className={cn('sidebar', isCollapsed && 'sidebar--collapsed')}>
      <div className="sidebar__content">
        <div className="sidebar__brand">
          {isCollapsed ? (
            <button
              type="button"
              className="sidebar__brand-button"
              onClick={handleOpenSidebar}
              aria-label={strings.sidebar.lock}
              title={strings.sidebar.lock}
            >
              <span className="sidebar__brand-mark" aria-hidden="true">
                <span className="sidebar__brand-letter">T</span>
                <span className="sidebar__brand-open-icon" aria-hidden="true">
                  <PanelLeftDashed size={16} strokeWidth={2} />
                </span>
              </span>
            </button>
          ) : (
            <>
              <Link
                to="/dashboard"
                className="sidebar__brand-link"
                aria-label={strings.sidebar.dashboard}
              >
                <span className="sidebar__brand-mark" aria-hidden="true">
                  <span className="sidebar__brand-letter">T</span>
                </span>
                <span className="sidebar__brand-name">Touzi</span>
              </Link>
              <button
                type="button"
                className="sidebar__brand-toggle"
                onClick={handleCloseSidebar}
                aria-label={strings.sidebar.unlock}
                title={strings.sidebar.unlock}
              >
                <PanelLeftDashed size={16} strokeWidth={2} />
              </button>
            </>
          )}
        </div>
        <nav className="sidebar__nav" aria-label={strings.sidebar.navAriaLabel}>
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.path === '/market'
                ? location.pathname.startsWith('/market')
                : location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn('sidebar__nav-link', isActive && 'sidebar__nav-link--active')}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="sidebar__nav-icon" aria-hidden="true">
                  <Icon size={18} strokeWidth={2} />
                </span>
                <span className="sidebar__nav-label">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="sidebar__footer">
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="sidebar__user"
                aria-label={strings.header.profileLabel}
              >
                <div className="sidebar__user-avatar">
                  <User size={16} />
                </div>
                <div className="sidebar__user-text">
                  <span className="sidebar__user-name">{primaryLabel}</span>
                </div>
                <ChevronDown size={14} className="sidebar__user-chevron" aria-hidden="true" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="sidebar__user-menu"
                align="start"
                side="top"
                sideOffset={8}
              >
                <DropdownMenu.Item
                  className="sidebar__user-menu-item"
                  onSelect={() => navigate('/settings')}
                >
                  <Settings size={16} />
                  <span>{strings.header.settingsLabel}</span>
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="sidebar__user-menu-separator" />
                <DropdownMenu.Item
                  className="sidebar__user-menu-item sidebar__user-menu-item--danger"
                  onSelect={handleSignOut}
                >
                  <LogOut size={16} />
                  <span>{strings.header.signOutLabel}</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
    </aside>
  );
}
